import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowLeftRight,
  CalendarClock,
  FlaskConical,
  Landmark,
  LayoutDashboard,
  LogOut,
  Radar,
  Settings,
  Tags,
  Upload,
  User,
} from 'lucide-react';
import { AppShell, BenachrichtigungenProvider, useBenachrichtigungen } from '@elbklar/ui';

import { useAuth } from '@/features/auth/AuthContext';
import { benachrichtigungenApi } from '@/lib/api';
import { kontoUrl } from '@/lib/konto';
import { RouterLink } from './RouterLink';

/**
 * Rahmen jeder angemeldeten Finanzen-Seite nach dem Elbklar Design System:
 * schwebende Sidebar links, Inhalt rechts, jede Seite bringt ihren eigenen
 * PageHeader mit. Unter 768 px die Tab-Leiste unten (Mobil, U4): Übersicht ·
 * Radar · Mehr, ohne „+“ — Finanzen hat kein Schnell erfassen. „Mehr“ zeigt
 * die übrigen Ziele und den Fuß der Sidebar.
 *
 * Benachrichtigungen kommen aus dem zentralen Modul von @elbklar/ui (MR N);
 * finance-api bindet dafür die Router aus core ein. Profil und die
 * Einstellungen der Benachrichtigungen liegen zentral auf konto.elbklar.com.
 */
export function FinanceLayout() {
  const navigate = useNavigate();
  return (
    <BenachrichtigungenProvider
      app="finance"
      api={benachrichtigungenApi}
      navigieren={navigate}
      kontoEinstellungen={() => kontoUrl('/benachrichtigungen')}
    >
      <Rahmen />
    </BenachrichtigungenProvider>
  );
}

function Rahmen() {
  const { pathname } = useLocation();
  const { logout } = useAuth();
  const benachrichtigungen = useBenachrichtigungen();

  // Erstes Pfadsegment = Bereich: /radar -> radar, / -> uebersicht.
  const aktiv = pathname.split('/')[1] || 'uebersicht';

  return (
    <AppShell
      sidebar={{
        app: 'FIN',
        search: false,
        activeId: aktiv,
        linkAs: RouterLink,
        sections: [
          { items: [{ id: 'uebersicht', label: 'Übersicht', icon: LayoutDashboard, href: '/' }] },
          {
            label: 'Planung',
            items: [
              { id: 'planung', label: 'Planung', icon: FlaskConical, href: '/planung' },
              { id: 'fixkosten', label: 'Fixkosten', icon: CalendarClock, href: '/fixkosten' },
              { id: 'kredit', label: 'Kredit', icon: Landmark, href: '/kredit' },
            ],
          },
          {
            label: 'Förderung',
            items: [{ id: 'radar', label: 'Radar', icon: Radar, href: '/radar' }],
          },
          {
            // U3: Buchungen, Import und Kategorien bleiben, als schlichte Seiten
            // am Rechner (Kategorien tragen Planposten und Fixkosten, der Import
            // ist der Handweg neben der Übernahme aus der Buchhaltung).
            label: 'Ist-Daten',
            items: [
              { id: 'buchungen', label: 'Buchungen', icon: ArrowLeftRight, href: '/buchungen' },
              { id: 'import', label: 'Import', icon: Upload, href: '/import' },
              { id: 'kategorien', label: 'Kategorien', icon: Tags, href: '/kategorien' },
            ],
          },
        ],
        // Benachrichtigungen zuerst, in jeder App derselbe Ort (Henri, 26.10.2026).
        // „Einstellungen“ gehört dem Werkzeug; Profil hängt am Menschen und liegt
        // auf konto.elbklar.com (ADR-0005).
        footer: [
          benachrichtigungen.eintrag,
          { id: 'einstellungen', label: 'Einstellungen', icon: Settings, href: '/einstellungen' },
          { id: 'profil', label: 'Profil', icon: User, href: kontoUrl('/profil') },
          { id: 'abmelden', label: 'Abmelden', icon: LogOut },
        ],
        onSelect: (id) => {
          if (benachrichtigungen.waehlen(id)) return;
          // logout() meldet beim zentralen Dienst ab und führt selbst zur Anmeldung.
          if (id === 'abmelden') void logout();
        },
      }}
      mobilNav={{ tabs: ['uebersicht', 'radar'] }}
    >
      <Outlet />
    </AppShell>
  );
}
